'use client';

import { useEffect, useState } from 'react';

/**
 * Discriminated state machine for a single async fetch. Used by every
 * host-coupled block to render loading / empty / loaded / error
 * states uniformly.
 *
 * <ul>
 *   <li>{@code loading} — fetch in flight or about to start.</li>
 *   <li>{@code empty} — fetch resolved to {@code null} or
 *       {@code undefined} (treated as "no content").</li>
 *   <li>{@code loaded} — fetch resolved with non-null data.</li>
 *   <li>{@code error} — fetch threw (not an abort).</li>
 * </ul>
 */
export type AsyncResourceState<T> =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'loaded'; data: T }
  | { kind: 'error'; error: Error };

/**
 * Minimal client-side fetch hook for host-coupled blocks. Wraps
 * useState + useEffect + AbortController in one call so block
 * components can render a uniform loading / empty / error UI.
 *
 * <p>Deps array controls re-fetching the same way useEffect does —
 * pass the prop values that shape the fetch (source, limit, slug,
 * etc.). When the deps change, the in-flight request is aborted and
 * a new one starts.
 *
 * <p>When {@code fetcher} resolves to {@code null} or
 * {@code undefined}, the state is {@code empty}, not {@code loaded}
 * — so blocks can branch on it cleanly without writing
 * {@code data == null} guards.
 */
export function useAsyncResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T | null | undefined>,
  deps: readonly unknown[],
): AsyncResourceState<T> {
  const [state, setState] = useState<AsyncResourceState<T>>({ kind: 'loading' });

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: 'loading' });

    fetcher(ctrl.signal)
      .then((data) => {
        if (ctrl.signal.aborted) return;
        if (data == null) {
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'loaded', data });
        }
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ kind: 'error', error });
      });

    return () => ctrl.abort();
  }, deps);

  return state;
}
